<template>
  <div>
    <!-- Toolbar -->
    <div class="toolbar">
      <div class="toolbar-left">
        <select v-model="filterIsActive" @change="() => { currentPage = 1; loadGroups() }" class="filter-select">
          <option value="">{{ $t('bo.platformSkills.allStatuses') }}</option>
          <option value="true">{{ $t('bo.common.active') }}</option>
          <option value="false">{{ $t('bo.common.archived') }}</option>
        </select>
        <select v-model="filterType" @change="() => { currentPage = 1; loadGroups() }" class="filter-select">
          <option value="">{{ $t('bo.platformSkills.allTypes') }}</option>
          <option value="domain">{{ $t('bo.platformSkills.domain') }}</option>
          <option value="transversal">{{ $t('bo.platformSkills.transversal') }}</option>
        </select>
      </div>
      <button @click="showCreate = true" class="btn-primary">{{ $t('bo.platformSkills.newGroup') }}</button>
    </div>

    <!-- Loading -->
    <div v-if="loading" class="state-msg">{{ $t('bo.platformSkills.loadingGroups') }}</div>
    <div v-else-if="error" class="error-msg">{{ error }}</div>
    <div v-else-if="groups.length === 0" class="state-msg">{{ $t('bo.platformSkills.noGroupsFound') }}</div>

    <!-- Table -->
    <table v-else class="table">
      <thead>
        <tr>
          <th>{{ $t('bo.platformSkills.colName') }}</th>
          <th>{{ $t('bo.platformSkills.colSlug') }}</th>
          <th>{{ $t('bo.platformSkills.colGroupType') }}</th>
          <th>{{ $t('bo.platformSkills.colSkills') }}</th>
          <th>{{ $t('bo.platformSkills.colEscoMappings') }}</th>
          <th>{{ $t('bo.platformSkills.colStatus') }}</th>
          <th>{{ $t('bo.platformSkills.colActions') }}</th>
        </tr>
      </thead>
      <tbody>
        <tr v-for="group in groups" :key="group.id">
          <td class="col-name">
            <div class="name">{{ group.name }}</div>
            <!-- Nom francais (skill_groups_i18n), ou mention a completer. -->
            <div v-if="group.translations?.fr?.name" class="name-fr">FR · {{ group.translations.fr.name }}</div>
            <div v-else class="name-fr missing">{{ $t('bo.platformSkills.translation.missing') }}</div>
            <div v-if="group.description" class="desc">{{ truncate(group.description, 70) }}</div>
          </td>
          <td><code class="slug">{{ group.slug || '—' }}</code></td>
          <td>
            <span :class="['badge', `badge-type-${group.group_type}`]">{{ group.group_type || 'domain' }}</span>
          </td>
          <td>{{ group.skills_count ?? '—' }}</td>
          <td>{{ (group.esco_mappings || []).length }}</td>
          <td>
            <span :class="['badge', group.is_active ? 'badge-active' : 'badge-archived']">
              {{ group.is_active ? $t('bo.common.active') : $t('bo.common.archived') }}
            </span>
          </td>
          <td class="actions">
            <button @click="openEdit(group)" class="btn-action btn-edit">{{ $t('bo.common.edit') }}</button>
            <button
              v-if="group.is_active"
              @click="handleArchive(group)"
              class="btn-action btn-archive"
              :disabled="actionLoading === group.id"
            >{{ $t('bo.common.archive') }}</button>
          </td>
        </tr>
      </tbody>
    </table>

    <!-- Pagination -->
    <div v-if="meta && meta.last_page > 1" class="pagination">
      <button :disabled="meta.current_page === 1" @click="goToPage(meta.current_page - 1)" class="page-btn">‹</button>
      <span class="page-info">{{ $t('bo.platformSkills.pageInfo', { current: meta.current_page, last: meta.last_page, total: meta.total }) }}</span>
      <button :disabled="meta.current_page === meta.last_page" @click="goToPage(meta.current_page + 1)" class="page-btn">›</button>
    </div>

    <!-- Create modal -->
    <CreateSkillGroupModal
      v-if="showCreate"
      @close="showCreate = false"
      @created="handleCreated"
    />

    <!-- Edit modal -->
    <EditSkillGroupModal
      v-if="editingGroup"
      :group="editingGroup"
      @close="editingGroup = null"
      @updated="handleUpdated"
    />
  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue'
import { useI18n } from 'vue-i18n'
import platformSkillService from '@/services/platformSkillService'
import CreateSkillGroupModal from './CreateSkillGroupModal.vue'
import EditSkillGroupModal from './EditSkillGroupModal.vue'

const { t } = useI18n()

const groups = ref([])
const meta = ref(null)
const currentPage = ref(1)
const loading = ref(false)
const error = ref('')
const filterIsActive = ref('true')
const filterType = ref('')
const actionLoading = ref(null)
const showCreate = ref(false)
const editingGroup = ref(null)

const loadGroups = async () => {
  loading.value = true
  error.value = ''
  try {
    // platform_only : n'afficher que les groupes de la plateforme, pas les groupes ESCO importes.
    const params = { page: currentPage.value, has_skills: true, platform_only: true }
    if (filterIsActive.value !== '') params.is_active = filterIsActive.value
    if (filterType.value) params.group_type = filterType.value
    const res = await platformSkillService.getGroups(params)
    groups.value = res.data || []
    meta.value = res.meta || null
  } catch (e) {
    error.value = t('bo.platformSkills.failedLoadGroups')
  } finally {
    loading.value = false
  }
}

const goToPage = (page) => {
  currentPage.value = page
  loadGroups()
}

const openEdit = (group) => {
  editingGroup.value = group
}

const handleArchive = async (group) => {
  if (!confirm(t('bo.platformSkills.confirmArchiveGroup', { name: group.name }))) return
  actionLoading.value = group.id
  try {
    await platformSkillService.archiveGroup(group.id)
    await loadGroups()
  } catch (e) {
    alert(e.response?.data?.message || t('bo.platformSkills.failedArchiveGroup'))
  } finally {
    actionLoading.value = null
  }
}

const handleCreated = () => {
  showCreate.value = false
  loadGroups()
}

const handleUpdated = () => {
  editingGroup.value = null
  loadGroups()
}

const truncate = (str, len) => str?.length > len ? str.substring(0, len) + '…' : str

defineExpose({ loadGroups })
onMounted(loadGroups)
</script>

<style scoped>
.toolbar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1rem;
}

.toolbar-left {
  display: flex;
  gap: 0.75rem;
}

.filter-select {
  padding: 0.5rem 0.75rem;
  border: 1px solid #d1d5db;
  border-radius: 0.375rem;
  font-size: 0.875rem;
  background: white;
}

.btn-primary {
  padding: 0.5rem 1rem;
  background: #3b82f6;
  color: white;
  border: none;
  border-radius: 0.375rem;
  font-size: 0.875rem;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.15s;
}

.btn-primary:hover { background: #2563eb; }

.state-msg {
  padding: 3rem;
  text-align: center;
  color: #6b7280;
  background: white;
  border-radius: 0.75rem;
  border: 1px solid #e5e7eb;
}

.error-msg {
  padding: 1rem 1.25rem;
  color: #dc2626;
  background: white;
  border-radius: 0.75rem;
  border: 1px solid #fecaca;
}

.table {
  width: 100%;
  border-collapse: collapse;
  font-size: 0.875rem;
  background: white;
  border-radius: 0.75rem;
  border: 1px solid #e5e7eb;
  overflow: hidden;
}

.table th {
  padding: 0.75rem 1rem;
  text-align: left;
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: #6b7280;
  background: #f9fafb;
  border-bottom: 1px solid #e5e7eb;
}

.table td {
  padding: 0.875rem 1rem;
  border-bottom: 1px solid #f3f4f6;
  vertical-align: top;
}

.table tr:last-child td { border-bottom: none; }

.col-name { max-width: 260px; }
.name { font-weight: 600; color: #1f2937; }
.desc { color: #6b7280; font-size: 0.8rem; margin-top: 0.2rem; }

.slug {
  font-size: 0.8rem;
  background: #f3f4f6;
  padding: 0.15rem 0.4rem;
  border-radius: 0.25rem;
  color: #374151;
}

.badge {
  display: inline-block;
  padding: 0.2rem 0.6rem;
  border-radius: 9999px;
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: capitalize;
}

.badge-active    { background: #d1fae5; color: #065f46; }
.badge-archived  { background: #f3f4f6; color: #6b7280; }
.badge-type-domain      { background: #eff6ff; color: #1d4ed8; }
.badge-type-transversal { background: #faf5ff; color: #7c3aed; }

.actions { display: flex; gap: 0.5rem; }

.btn-action {
  padding: 0.3rem 0.65rem;
  border-radius: 0.375rem;
  font-size: 0.8rem;
  cursor: pointer;
  border: 1px solid;
  font-weight: 500;
  transition: all 0.15s;
}

.btn-action:disabled { opacity: 0.5; cursor: not-allowed; }
.btn-edit    { border-color: #3b82f6; color: #3b82f6; background: white; }
.btn-edit:hover:not(:disabled) { background: #eff6ff; }
.btn-archive { border-color: #f59e0b; color: #f59e0b; background: white; }
.btn-archive:hover:not(:disabled) { background: #fffbeb; }

.pagination {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 1rem;
  margin-top: 1rem;
}

.page-btn {
  padding: 0.35rem 0.75rem;
  border: 1px solid #d1d5db;
  border-radius: 0.375rem;
  background: white;
  cursor: pointer;
  font-size: 1rem;
  color: #374151;
}
.page-btn:disabled { opacity: 0.4; cursor: not-allowed; }
.page-btn:hover:not(:disabled) { background: #f3f4f6; }

.page-info { font-size: 0.875rem; color: #6b7280; }

.name-fr { color: #4b5563; font-size: 0.8rem; margin-top: 0.15rem; }
.name-fr.missing { color: #b45309; }
</style>
